package com.neurobs.ccabserver.administrator.components

import com.neurobs.ccab.{AccountSubjectResponse, CCABServer}
import com.neurobs.errors.Result
import com.neurobs.react.{CircuitState, GapSize, HoriAlign, LTFullRow, LayoutTable, Radio, RadioState, StyleUtil}
import diode.ActionResult.ModelUpdate
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.web.html.{className, div, style}
import diode._

import scala.concurrent.Future

/**
 * Handles the subject selection dialog used by Examiner when starting a session.
 */
object SessionSubjectSelectDialog {

	private val existingSubjectCaption = "Existing subject"
	private val newSubjectCaption = "New subject"

	case class Model
	(
		existingSubject: RadioState[Boolean]
	)

	case class SetExistingSubject(i: Int) extends Action

	class AppCircuit(val initialModel: Model) extends Circuit[Model] {
		override val actionHandler: HandlerFunction = (model, action) => action match {
			case SetExistingSubject(i) => Some(ModelUpdate(model.copy(existingSubject = model.existingSubject.newSelection(i))))
			case _ => None
		}
	}

	def initialModel: Model = Model(existingSubject = RadioState.booleanRadio(value = true, existingSubjectCaption, newSubjectCaption))

	@react object View {
		/**
		 * @param server       CCABServer to call
		 * @param accountId    Account to create the account-subject for
		 * @param dataPassword Data password for this account
		 * @param onFinished   Call back receiving new subject data, or None if cancelled
		 */
		case class Props
		(
			server: CCABServer,
			accountId: Long,
			dataPassword: String,
			onFinished: Option[AccountSubjectResponse] => Unit
		)

		val component = FunctionalComponent[Props] { props =>
			val state = CircuitState[Model](() => initialModel, new AppCircuit(_))
			val existing = state.value.existingSubject.value

			def loadSubjects: Future[Result[Seq[AccountSubjectResponse]]] = {
				props.server.getSubjects(accountId = props.accountId, dataPassword = props.dataPassword)._2
			}

			LayoutTable(gapSize = GapSize.Large, fullWidth = false)(
				LTFullRow(align = HoriAlign.Center)(div(className := titleClass)("Subject Selection")),
				LTFullRow(align = HoriAlign.Center)(Radio(state.value.existingSubject.generateProperties(), i => state.dispatch(SetExistingSubject(i)))),
				LTFullRow(align = HoriAlign.Center)(
					div(style := StyleUtil.visibleStyle(existing))(
						SubjectSelectDialog.View(() => loadSubjects, props.onFinished)
					),
					div(style := StyleUtil.visibleStyle(!existing))(
						CreateSubjectDialog(
							server = props.server,
							accountId = props.accountId,
							dataPassword = props.dataPassword,
							onFinished = props.onFinished,
							showTitle = false
						)
					)
				)
			)
		}

		private val titleClass = "dlg-title"
	}
}